import { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { Loader2, ChevronDown } from 'lucide-react';
import { MessageItem } from './MessageItem';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { TelegramMessage } from '../types';

interface MessageListProps {
  messages: TelegramMessage[];
  chatId: number | null;
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  selectedIds: Set<number>;
  onToggleSelection: (messageId: number, shiftKey: boolean) => void;
  onContextMenu: (e: React.MouseEvent, message: TelegramMessage) => void;
  isMessageRead: (message: TelegramMessage) => boolean;
  onLoadMore?: () => Promise<boolean>;
}

// Порог в пикселях для триггера загрузки (когда до верха осталось меньше этого значения)
const LOAD_MORE_THRESHOLD = 200;
// Порог для показа кнопки "scroll to bottom"
const SHOW_SCROLL_BUTTON_THRESHOLD = 400;

export function MessageList({
  messages,
  chatId,
  isLoading,
  isLoadingMore = false,
  hasMore = true,
  selectedIds,
  onToggleSelection,
  onContextMenu,
  isMessageRead,
  onLoadMore,
}: MessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isInitialLoadRef = useRef(true);
  const lastMessageIdRef = useRef<number | null>(null);
  const isLoadingMoreRef = useRef(false);

  // Состояние для кнопки scroll-to-bottom
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Получаем ID последнего (самого нового) сообщения
  const latestMessageId = useMemo(() => {
    if (messages.length === 0) {return null;}
    const sortedMessages = [...messages].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    return sortedMessages[0]?.id ?? null;
  }, [messages]);

  // Скролл вниз
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
    setUnreadCount(0);
  }, []);

  // Обработка подгрузки старых сообщений - сохраняем позицию
  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl || messages.length === 0) {return;}

    // Если это первая загрузка или смена чата - скроллим вниз
    if (isInitialLoadRef.current) {
      // Используем setTimeout чтобы DOM успел отрендериться
      setTimeout(() => {
        scrollToBottom('instant');
      }, 0);
      isInitialLoadRef.current = false;
      lastMessageIdRef.current = latestMessageId;
      return;
    }

    // Если только что закончилась подгрузка старых сообщений - ничего не делаем
    // Позиция уже была сохранена в handleLoadMoreComplete
    if (isLoadingMoreRef.current) {
      return;
    }

    // Если появилось новое сообщение (не старое при подгрузке)
    if (latestMessageId !== lastMessageIdRef.current) {
      // Проверяем, находимся ли мы близко к низу
      const distanceFromBottom = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
      const isNearBottom = distanceFromBottom < SHOW_SCROLL_BUTTON_THRESHOLD;

      if (isNearBottom) {
        // Если близко к низу - автоматически скроллим
        scrollToBottom('smooth');
      } else {
        // Если далеко от низа - увеличиваем счетчик непрочитанных
        setUnreadCount(prev => prev + 1);
      }

      lastMessageIdRef.current = latestMessageId;
    }
  }, [messages, latestMessageId, scrollToBottom]);

  // При смене чата сбрасываем состояние
  useEffect(() => {
    isInitialLoadRef.current = true;
    lastMessageIdRef.current = null;
    setShowScrollButton(false);
    setUnreadCount(0);
  }, [chatId]);

  // Обработчик завершения подгрузки - сохраняем позицию скролла
  const handleLoadMoreComplete = useCallback((prevScrollHeight: number) => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) {return;}

    // Восстанавливаем позицию: новая высота минус старая = сколько добавилось сверху
    const newScrollHeight = scrollEl.scrollHeight;
    const scrollDiff = newScrollHeight - prevScrollHeight;
    scrollEl.scrollTop = scrollDiff;

    isLoadingMoreRef.current = false;
  }, []);

  // Обработчик скролла
  const handleScroll = useCallback(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) {return;}

    // Проверяем, нужно ли показывать кнопку scroll-to-bottom
    const distanceFromBottom = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
    setShowScrollButton(distanceFromBottom > SHOW_SCROLL_BUTTON_THRESHOLD);

    // Если близко к низу - сбрасываем счетчик непрочитанных
    if (distanceFromBottom < 100) {
      setUnreadCount(0);
    }

    // Infinite scroll - загрузка старых сообщений
    if (isLoadingMore || !hasMore || !onLoadMore || isLoadingMoreRef.current) {return;}

    // Если доскроллили близко к верху — загружаем ещё
    if (scrollEl.scrollTop < LOAD_MORE_THRESHOLD) {
      isLoadingMoreRef.current = true;
      const prevScrollHeight = scrollEl.scrollHeight;

      onLoadMore().finally(() => {
        // Небольшая задержка чтобы DOM обновился
        requestAnimationFrame(() => {
          handleLoadMoreComplete(prevScrollHeight);
        });
      });
    }
  }, [isLoadingMore, hasMore, onLoadMore, handleLoadMoreComplete]);

  // Map for quick access to message refs by ID
  const messageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Scroll to specific message by ID
  const scrollToMessage = useCallback((messageId: number) => {
    const element = messageRefs.current.get(messageId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight briefly
      element.classList.add('animate-pulse', 'bg-primary/20');
      setTimeout(() => {
        element.classList.remove('animate-pulse', 'bg-primary/20');
      }, 2000);
    }
  }, []);

  // Group messages by date
  const groupedMessages = useMemo(() => groupMessagesByDate(messages), [messages]);

  if (isLoading && messages.length === 0) {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center text-muted-foreground">
        Нет сообщений
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 relative">
      <div
        className="h-full overflow-auto telegram-scrollbar-hidden"
        ref={scrollRef}
        onScroll={handleScroll}
      >
        <div className="p-4">
          {/* Loading indicator for older messages (top) */}
          {isLoadingMore && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Загрузка...</span>
            </div>
          )}

          {/* No more messages indicator */}
          {!hasMore && messages.length > 0 && (
            <div className="flex items-center justify-center py-4">
              <span className="text-xs text-muted-foreground">Начало переписки</span>
            </div>
          )}

          {groupedMessages.map(({ date, messages: dayMessages }) => (
            <div key={date}>
              {/* Date separator */}
              <div className="flex items-center justify-center my-4">
                <span className="bg-muted px-3 py-1 rounded-full text-xs text-muted-foreground">
                  {formatDateSeparator(date)}
                </span>
              </div>

              {/* Messages for this date, grouped by sender */}
              {groupMessagesBySender(dayMessages).map((group, groupIndex) => (
                <div key={`${group.fromId}-${groupIndex}`}>
                  {group.messages.map((message, index) => (
                    <MessageItem
                      key={message.id}
                      ref={(el) => {
                        if (el) {
                          messageRefs.current.set(message.id, el);
                        } else {
                          messageRefs.current.delete(message.id);
                        }
                      }}
                      message={message}
                      chatId={chatId!}
                      isSelected={selectedIds.has(message.id)}
                      isRead={isMessageRead(message)}
                      onClick={(e) => onToggleSelection(message.id, e.shiftKey)}
                      onContextMenu={(e) => onContextMenu(e, message)}
                      onScrollToMessage={scrollToMessage}
                      showAvatar={index === 0 && !group.isOutgoing}
                      showSenderName={index === 0 && !group.isOutgoing}
                    />
                  ))}
                  {/* Add spacing between different senders' message groups */}
                  {!group.isOutgoing && groupIndex < groupMessagesBySender(dayMessages).length - 1 && (
                    <div className="h-2" />
                  )}
                </div>
              ))}
            </div>
          ))}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Scroll to bottom button */}
      <div
        className={cn(
          'absolute bottom-4 right-4 transition-all duration-200',
          showScrollButton ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        )}
      >
        <Button
          variant="secondary"
          size="icon"
          className="h-10 w-10 rounded-full shadow-lg relative"
          onClick={() => scrollToBottom('smooth')}
        >
          <ChevronDown className="h-5 w-5" />

          {/* Unread count badge */}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs font-medium min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </div>
    </div>
  );
}

interface GroupedMessages {
  date: string;
  messages: TelegramMessage[];
}

interface MessageGroup {
  fromId: number;
  isOutgoing: boolean;
  messages: TelegramMessage[];
}

function groupMessagesByDate(messages: TelegramMessage[]): GroupedMessages[] {
  const groups: Map<string, TelegramMessage[]> = new Map();

  // Sort messages by date (oldest first for display)
  const sortedMessages = [...messages].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  for (const message of sortedMessages) {
    const date = new Date(message.date).toDateString();
    if (!groups.has(date)) {
      groups.set(date, []);
    }
    groups.get(date)!.push(message);
  }

  return [...groups.entries()].map(([date, messages]) => ({
    date,
    messages,
  }));
}

/**
 * Группирует сообщения по отправителю
 * Сообщения от одного отправителя, которые идут подряд, группируются вместе
 * Это позволяет показывать аватарку и имя только у первого сообщения группы
 *
 * Исходящие сообщения НЕ группируются (так как пользователь только один)
 */
function groupMessagesBySender(messages: TelegramMessage[]): MessageGroup[] {
  const groups: MessageGroup[] = [];

  for (const message of messages) {
    const lastGroup = groups.at(-1);
    const isOutgoing = message.isOutgoing ?? false;

    // Для исходящих сообщений - каждое в своей группе
    // Для входящих - группируем от одного отправителя
    const shouldNewGroup =
      !lastGroup ||
      isOutgoing ||
      lastGroup.isOutgoing ||
      lastGroup.fromId !== message.fromId;

    if (shouldNewGroup) {
      groups.push({
        fromId: message.fromId,
        isOutgoing,
        messages: [message],
      });
    } else {
      // Добавляем в существующую группу входящих сообщений
      lastGroup.messages.push(message);
    }
  }

  return groups;
}

function formatDateSeparator(dateString: string): string {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Сегодня';
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Вчера';
  }

  return date.toLocaleDateString('ru', {
    day: 'numeric',
    month: 'long',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  });
}
